import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Trophy,
  Calendar,
  TrendingUp,
  Users,
  Medal,
  Clock,
  ArrowRight,
  MapPin,
} from "lucide-react";
import { getUserByEmail } from "../lib/api/users";
import {
  listEvents,
  listEventStandings,
  listUsers,
  listLeagues,
  listEventPlayerResults,
  listEventTopCutResults,
  listLeaguePointAdjustments,
  listPlayerConstructs,
} from "../graphql/queries";
import { formatDate } from "../utils/dateUtils";
import { getCurrentLeague } from "../lib/leagueUtils";
import {
  applyPremiumMultiplier,
  calculateParticipationPoints,
  calculateRecordPoints,
  calculateTop8Points,
  getLeagueScoring,
} from "../lib/scoring";

const Dashboard: React.FC = () => {
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const location = useLocation();
  const client = generateClient();

  // Get current user data
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const storedToken = sessionStorage.getItem("id_token");
      if (!storedToken) return null;

      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));
        const userEmail = payload.email;

        if (!userEmail) return null;

        const user = await getUserByEmail(userEmail);
        return user;
      } catch (error) {
        console.error("Error getting user from token:", error);
        return null;
      }
    },
  });

  // Get all leagues for navigation and data selection
  const { data: leagues } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });

  // Fetch all events
  const { data: allEvents } = useQuery({
    queryKey: ["allEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });

  // Find the league with the most events/data for dashboard display
  const defaultLeagueId = useMemo(() => {
    if (!leagues || !allEvents) return null;

    // Count events per league
    const leagueEventCounts = leagues.reduce(
      (acc: Record<string, number>, league: any) => {
        const eventCount = allEvents.filter(
          (event: any) => event.leagueId === league.id
        ).length;
        acc[league.id] = eventCount;
        return acc;
      },
      {}
    );

    // Find the league with the most events
    const leagueWithMostEvents = Object.entries(leagueEventCounts)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .find(([_, count]) => count > 0);

    if (leagueWithMostEvents) {
      return leagueWithMostEvents[0];
    }

    // Fallback to current league by date if no league has events
    const currentLeague = getCurrentLeague(leagues as any);
    return currentLeague?.id;
  }, [leagues, allEvents]);
  const scoring = useMemo(
    () =>
      getLeagueScoring(
        leagues?.find((league: any) => league.id === defaultLeagueId)
      ),
    [defaultLeagueId, leagues]
  );

  // Fetch all event standings
  const { data: allStandings } = useQuery({
    queryKey: ["allEventStandings"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: { limit: 1000 },
      });
      return result.data.listEventStandings.items;
    },
  });

  // Fetch all users for standings calculation
  const { data: allUsers } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  // Fetch all event player results (from EventLink imports)
  const { data: allEventPlayerResults } = useQuery({
    queryKey: ["allEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });

  // Fetch all top cut results
  const { data: allTopCutResults } = useQuery({
    queryKey: ["allTopCutResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventTopCutResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventTopCutResults.items;
    },
  });

  // Fetch all player constructs
  const { data: allPlayerConstructs } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  const { data: allPointAdjustments } = useQuery({
    queryKey: ["allLeaguePointAdjustments"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeaguePointAdjustments,
        variables: { limit: 1000 },
      });
      return result.data.listLeaguePointAdjustments.items;
    },
  });

  // Calculate user's league standings
  const userStandings = useMemo(() => {
    if (
      !currentUser ||
      !allStandings ||
      !allEvents ||
      !allUsers ||
      !allEventPlayerResults ||
      !allTopCutResults ||
      !allPlayerConstructs ||
      !allPointAdjustments ||
      !defaultLeagueId
    )
      return null;

    // Get events for the default league
    const leagueEvents = allEvents.filter(
      (event: any) => event.leagueId === defaultLeagueId
    );
    const leagueEventIds = leagueEvents.map((event: any) => event.id);

    // Calculate league standings (same logic as Standings page)
    const userStandingsMap: Record<string, any> = {};

    // Process traditional event standings
    const leagueStandingsData = allStandings.filter((standing: any) =>
      leagueEventIds.includes(standing.eventId)
    );

    leagueStandingsData.forEach((standing: any) => {
      if (!userStandingsMap[standing.userId]) {
        userStandingsMap[standing.userId] = {
          userId: standing.userId,
          user: allUsers.find((u: any) => u.id === standing.userId),
          totalPoints: 0,
          totalEvents: 0,
          bestRank: Infinity,
          totalRanks: 0,
          events: [],
        };
      }

      const userStanding = userStandingsMap[standing.userId];
      const event = leagueEvents.find((e: any) => e.id === standing.eventId);
      const isPremium = event?.isPremium === true;
      const pointsToAdd = applyPremiumMultiplier(
        standing.points,
        isPremium,
        scoring
      );

      userStanding.totalPoints += pointsToAdd;
      userStanding.totalEvents += 1;
      userStanding.bestRank = Math.min(userStanding.bestRank, standing.rank);
      userStanding.totalRanks += standing.rank;

      userStanding.events.push({
        eventId: standing.eventId,
        rank: standing.rank,
        points: standing.points,
        pointsAwarded: pointsToAdd,
        omw: standing.omw,
        gw: standing.gw,
        ogw: standing.ogw,
        isPremium: isPremium,
      });
    });

    // Process imported event player results
    const leagueEventPlayerResults = allEventPlayerResults.filter(
      (result: any) => leagueEventIds.includes(result.eventId)
    );

    leagueEventPlayerResults.forEach((result: any) => {
      const playerConstruct = allPlayerConstructs.find(
        (pc: any) => pc.id === result.playerConstructId
      );
      if (!playerConstruct) return;

      // Check if this player construct is mapped to a user
      if (playerConstruct.userId) {
        const userId = playerConstruct.userId;

        if (!userStandingsMap[userId]) {
          userStandingsMap[userId] = {
            userId: userId,
            user: allUsers.find((u: any) => u.id === userId),
            totalPoints: 0,
            totalEvents: 0,
            bestRank: Infinity,
            totalRanks: 0,
            events: [],
          };
        }

        const userStanding = userStandingsMap[userId];
        const event = leagueEvents.find((e: any) => e.id === result.eventId);
        const isPremium = event?.isPremium === true;
        const pointsToAdd = applyPremiumMultiplier(
          event?.format === "Commander"
            ? result.points || 0
            : calculateRecordPoints(
                result.wins || 0,
                result.draws || 0,
                result.losses || 0,
                scoring
              ),
          isPremium,
          scoring
        );

        userStanding.totalPoints += pointsToAdd;
        userStanding.totalEvents += 1;
        userStanding.bestRank = Math.min(userStanding.bestRank, result.rank);
        userStanding.totalRanks += result.rank;

        userStanding.events.push({
          eventId: result.eventId,
          rank: result.rank,
          points: result.points,
          pointsAwarded: pointsToAdd,
          omw: result.omw,
          gw: result.gw,
          ogw: result.ogw,
          wins: result.wins,
          losses: result.losses,
          draws: result.draws,
          isPremium: isPremium,
        });
      }
    });

    // Process top cut results
    const leagueTopCutResults = allTopCutResults.filter((result: any) =>
      leagueEventIds.includes(result.eventId) &&
      leagueEvents.find((event: any) => event.id === result.eventId)?.format !==
        "Commander"
    );

    leagueTopCutResults.forEach((result: any) => {
      let userStanding;

      if (result.userId) {
        // Top cut result for a user
        userStanding = userStandingsMap[result.userId];
      } else if (result.playerConstructId) {
        // Top cut result for a player construct
        const playerConstruct = allPlayerConstructs.find(
          (pc: any) => pc.id === result.playerConstructId
        );
        if (playerConstruct?.userId) {
          userStanding = userStandingsMap[playerConstruct.userId];
        }
      }

      if (userStanding) {
        const event = leagueEvents.find((e: any) => e.id === result.eventId);
        const isPremium = event?.isPremium === true;
        const bonusPointsToAdd = applyPremiumMultiplier(
          calculateTop8Points(result.rank, scoring),
          isPremium,
          scoring
        );

        userStanding.totalPoints += bonusPointsToAdd;
        userStanding.events.push({
          eventId: result.eventId,
          rank: result.rank,
          points: 0,
          pointsAwarded: bonusPointsToAdd,
          omw: 0,
          gw: 0,
          ogw: 0,
          isPremium: isPremium,
          isTopCut: true,
        });
      }
    });

    const currentUserConstructIds = new Set(
      allPlayerConstructs
        .filter((construct: any) => construct.userId === currentUser.id)
        .map((construct: any) => construct.id)
    );
    const adjustmentPoints = allPointAdjustments
      .filter(
        (adjustment: any) =>
          adjustment.leagueId === defaultLeagueId &&
          (adjustment.userId === currentUser.id ||
            currentUserConstructIds.has(adjustment.playerConstructId))
      )
      .reduce(
        (sum: number, adjustment: any) => sum + adjustment.points,
        0
      );
    if (adjustmentPoints && userStandingsMap[currentUser.id]) {
      userStandingsMap[currentUser.id].totalPoints += adjustmentPoints;
      userStandingsMap[currentUser.id].adjustmentPoints = adjustmentPoints;
    }

    // Add participation bonus and calculate averages
    Object.values(userStandingsMap).forEach((standing: any) => {
      standing.totalPoints += calculateParticipationPoints(
        standing.totalEvents,
        scoring
      );
      standing.averageRank = standing.totalRanks / standing.totalEvents;

      // Calculate comprehensive match statistics
      const totalWins = standing.events.reduce(
        (sum: number, event: any) => sum + (event.wins || 0),
        0
      );
      const totalLosses = standing.events.reduce(
        (sum: number, event: any) => sum + (event.losses || 0),
        0
      );
      const totalDraws = standing.events.reduce(
        (sum: number, event: any) => sum + (event.draws || 0),
        0
      );
      const totalMatches = totalWins + totalLosses + totalDraws;

      // Calculate win percentages
      standing.totalWins = totalWins;
      standing.totalLosses = totalLosses;
      standing.totalDraws = totalDraws;
      standing.totalMatches = totalMatches;
      standing.matchWinPercentage =
        totalMatches > 0 ? (totalWins / totalMatches) * 100 : 0;

      // Calculate average percentages from EventPlayerResult data
      const eventsWithStats = standing.events.filter(
        (event: any) => event.gw !== undefined && event.gw > 0
      );
      if (eventsWithStats.length > 0) {
        standing.gameWinPercentage =
          eventsWithStats.reduce(
            (sum: number, event: any) => sum + event.gw,
            0
          ) / eventsWithStats.length;
        standing.opponentMatchWinPercentage =
          eventsWithStats.reduce(
            (sum: number, event: any) => sum + event.omw,
            0
          ) / eventsWithStats.length;
        standing.opponentGameWinPercentage =
          eventsWithStats.reduce(
            (sum: number, event: any) => sum + event.ogw,
            0
          ) / eventsWithStats.length;
      } else {
        standing.gameWinPercentage = 0;
        standing.opponentMatchWinPercentage = 0;
        standing.opponentGameWinPercentage = 0;
      }
    });

    // Sort standings and find user's position
    const sortedStandings = Object.values(userStandingsMap).sort(
      (a: any, b: any) => b.totalPoints - a.totalPoints
    );

    const userStanding = sortedStandings.find(
      (standing: any) => standing.userId === currentUser.id
    );

    if (!userStanding) return null;

    const userRank =
      sortedStandings.findIndex(
        (standing: any) => standing.userId === currentUser.id
      ) + 1;

    return {
      user: userStanding,
      rank: userRank,
      totalEvents: userStanding.totalEvents,
      totalPoints: userStanding.totalPoints,
      bestRank: userStanding.bestRank,
      averageRank: userStanding.averageRank,
      // Comprehensive match statistics
      totalWins: userStanding.totalWins,
      totalLosses: userStanding.totalLosses,
      totalDraws: userStanding.totalDraws,
      totalMatches: userStanding.totalMatches,
      matchWinPercentage: userStanding.matchWinPercentage,
      gameWinPercentage: userStanding.gameWinPercentage,
      opponentMatchWinPercentage: userStanding.opponentMatchWinPercentage,
      opponentGameWinPercentage: userStanding.opponentGameWinPercentage,
      events: userStanding.events,
    };
  }, [
    currentUser,
    allStandings,
    allEvents,
    allUsers,
    allEventPlayerResults,
    allTopCutResults,
    allPlayerConstructs,
    allPointAdjustments,
    defaultLeagueId,
    scoring,
  ]);

  // Get upcoming events
  const upcomingEvents = useMemo(() => {
    if (!allEvents) return [];

    const now = new Date();
    return allEvents
      .filter((event: any) => new Date(event.date) > now && !event.isCompleted)
      .sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      .slice(0, 3);
  }, [allEvents]);

  useEffect(() => {
    // Show welcome message if user just completed profile setup
    if (location.state?.fromProfileSetup) {
      setShowWelcomeMessage(true);
      setTimeout(() => setShowWelcomeMessage(false), 5000);
    }
  }, [location.state]);

  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      {showWelcomeMessage && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <p className="text-green-800">
                Welcome to MTG League Tracker! Your profile has been set up
                successfully.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Welcome Section */}
      <Card className="modern-card animated-bg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Trophy className="h-6 w-6" />
            Welcome, {currentUser?.username || "Player"}!
          </CardTitle>
          <CardDescription className="text-white/80">
            Track your progress, view upcoming events, and compete in the
            league.
          </CardDescription>
          {defaultLeagueId && leagues && (
            <div className="mt-2">
              <Badge
                variant="secondary"
                className="bg-white/20 text-white border-white/30"
              >
                <Trophy className="h-3 w-3 mr-1" />
                Showing data from:{" "}
                {leagues.find((l: any) => l.id === defaultLeagueId)?.name ||
                  "Current League"}
              </Badge>
            </div>
          )}
        </CardHeader>
      </Card>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Current Standings */}
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-green-400" />
              Current Standings
            </CardTitle>
            <CardDescription>Your position in the league</CardDescription>
          </CardHeader>
          <CardContent>
            {userStandings ? (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-3xl font-bold">
                    {userStandings.rank}
                  </span>
                  <span className="text-muted-foreground">
                    of {allUsers?.length || 0} players
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium">
                      {userStandings.totalPoints}
                    </div>
                    <div className="text-muted-foreground">Total Points</div>
                  </div>
                  <div>
                    <div className="font-medium">
                      {userStandings.totalEvents}
                    </div>
                    <div className="text-muted-foreground">Events</div>
                  </div>
                </div>
                {defaultLeagueId && (
                  <Button
                    asChild
                    className="gradient-button text-white border-0"
                    size="sm"
                  >
                    <Link to={`/league/${defaultLeagueId}/standings`}>
                      View Full Standings
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No standings data available
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Participate in events to see your standings
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-400" />
              Upcoming Events
            </CardTitle>
            <CardDescription>Next scheduled tournaments</CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents.map((event: any) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{event.name}</h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(event.date)}</span>
                        <MapPin className="h-3 w-3" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {event.format}
                    </Badge>
                  </div>
                ))}
                {defaultLeagueId && (
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                  >
                    <Link to={`/league/${defaultLeagueId}/events`}>
                      View All Events
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No upcoming events</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Check back later for new events
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comprehensive Stats */}
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Your Stats
            </CardTitle>
            <CardDescription>
              Your comprehensive league performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            {userStandings ? (
              <div className="space-y-4">
                {/* Basic Stats */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {userStandings.bestRank}
                    </div>
                    <div className="text-muted-foreground">Best Rank</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {userStandings.averageRank.toFixed(1)}
                    </div>
                    <div className="text-muted-foreground">Avg Rank</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {userStandings.totalPoints}
                    </div>
                    <div className="text-muted-foreground">Event Points</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {userStandings.totalEvents}
                    </div>
                    <div className="text-muted-foreground">Events</div>
                  </div>
                </div>

                {/* Match Record */}
                {userStandings.totalMatches > 0 ? (
                  <>
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-3 text-primary">
                        Match Record
                      </h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-500">
                            {userStandings.totalWins}
                          </div>
                          <div className="text-muted-foreground">Wins</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-red-500">
                            {userStandings.totalLosses}
                          </div>
                          <div className="text-muted-foreground">Losses</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-yellow-500">
                            {userStandings.totalDraws}
                          </div>
                          <div className="text-muted-foreground">Draws</div>
                        </div>
                      </div>
                    </div>

                    {/* Win Percentages */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-3 text-primary">
                        Win Percentages
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-xl font-bold text-primary">
                            {userStandings.matchWinPercentage.toFixed(1)}%
                          </div>
                          <div className="text-muted-foreground">
                            Match Win %
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-primary">
                            {userStandings.gameWinPercentage.toFixed(1)}%
                          </div>
                          <div className="text-muted-foreground">
                            Game Win %
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="border-t pt-4">
                    <div className="text-center py-4">
                      <div className="text-sm text-muted-foreground mb-2">
                        📊 Detailed match statistics not available
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Import EventLink data to see match records and win
                        percentages
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No stats available</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Participate in events to see your stats
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest tournament performances</CardDescription>
        </CardHeader>
        <CardContent>
          {userStandings && userStandings.events.length > 0 ? (
            <div className="space-y-3">
              {userStandings.events
                .map((event: any) => {
                  const eventData = allEvents?.find(
                    (e: any) => e.id === event.eventId
                  );
                  return {
                    ...event,
                    eventName: eventData?.name || "Unknown Event",
                    eventDate: eventData?.date,
                  };
                })
                .sort(
                  (a: any, b: any) =>
                    new Date(b.eventDate).getTime() -
                    new Date(a.eventDate).getTime()
                )
                .slice(0, 3)
                .map((event: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{event.eventName}</h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {event.eventDate
                            ? formatDate(event.eventDate)
                            : "Unknown Date"}
                        </span>
                        {event.isTopCut && (
                          <Badge variant="secondary" className="text-xs">
                            Top Cut
                          </Badge>
                        )}
                        {event.isPremium && (
                          <Badge
                            variant="default"
                            className="text-xs bg-yellow-100 text-yellow-800"
                          >
                            Premium
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          Rank {event.rank}
                        </Badge>
                        <span className="text-sm font-medium">
                          {event.pointsAwarded} pts
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No recent activity</p>
              <p className="text-sm text-muted-foreground mt-2">
                Your activity will appear here after participating in events
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
